import { ArrowUpRight, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Reveal } from "@/components/Motion";
import { API, SITE_ORIGIN, setJsonLd, removeJsonLd } from "@/lib/site";
import { TUNING_CATEGORIES } from "@/lib/tuningCategories";

export function EstimatedBadge({ testId }) {
  return (
    <span
      data-testid={testId}
      title="Estimated Stage 1 figure — based on engine, ECU and comparable application data"
      className="ml-2 inline-block translate-y-[-2px] border border-amber-300 bg-amber-50 px-1.5 py-0.5 align-middle font-mono text-[9px] font-bold uppercase tracking-[0.12em] text-amber-700"
    >
      Estimated
    </span>
  );
}

export default function TuningCategoryIndex({ category }) {
  const cat = TUNING_CATEGORIES[category];
  const [data, setData] = useState(null);
  const [miss, setMiss] = useState(false);

  useEffect(() => {
    setData(null);
    setMiss(false);
    axios
      .get(`${API}/tuning-database/categories/${category}/makes`)
      .then((r) => setData(r.data))
      .catch(() => setMiss(true));
  }, [category]);

  useEffect(() => {
    document.title = cat.indexTitle;
    document.querySelector('meta[name="description"]')?.setAttribute("content", cat.indexDesc);
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = `${SITE_ORIGIN}${cat.basePath}`;
    setJsonLd("tci-breadcrumb", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "Tuning Database", item: `${SITE_ORIGIN}/tuning-database` },
        { "@type": "ListItem", position: 3, name: cat.name, item: `${SITE_ORIGIN}${cat.basePath}` },
      ],
    });
    return () => removeJsonLd("tci-breadcrumb");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  useEffect(() => {
    if (!data) return;
    setJsonLd("tci-collection", {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: cat.indexH1,
      url: `${SITE_ORIGIN}${cat.basePath}`,
      isPartOf: { "@type": "WebSite", name: "Regal Tuning", url: `${SITE_ORIGIN}/` },
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: data.makes.length,
        itemListElement: data.makes.map((m, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: m.make,
          url: `${SITE_ORIGIN}${m.url}`,
        })),
      },
    });
    return () => removeJsonLd("tci-collection");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <main className="pt-32 sm:pt-40 pb-24" data-testid={`tc-index-${category}`}>
      <div className="container-regal">
        <nav className="mb-6 flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-regal-amber">Home</Link>
          <ChevronRight size={12} />
          <Link to="/tuning-database" className="hover:text-regal-amber" data-testid="tci-breadcrumb-db">Tuning Database</Link>
          <ChevronRight size={12} />
          <span className="text-neutral-300">{cat.name}</span>
        </nav>

        <p className="overline-label mb-4">{cat.indexOverline}</p>
        <h1 className="max-w-4xl font-display text-3xl sm:text-5xl font-extrabold uppercase leading-[1.05] tracking-tight text-ink" data-testid="tci-h1">
          {cat.indexH1}
        </h1>
        <p className="mt-5 max-w-2xl text-sm sm:text-base text-neutral-400 leading-relaxed" data-testid="tci-intro">
          {cat.indexIntro}
        </p>

        {data && (
          <div className="mt-5 flex flex-wrap gap-2" data-testid="tci-stats">
            <span className="border border-line bg-surface px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-neutral-500" data-testid="tci-stat-makes">{data.makes.length} manufacturers</span>
            <span className="border border-line bg-surface px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-neutral-500" data-testid="tci-stat-vehicles">{data.total_vehicles.toLocaleString()} {cat.vehicleNounPlural} with published figures</span>
          </div>
        )}

        {miss && (
          <div className="mt-10 border border-line bg-surface p-10 text-center" data-testid="tci-error">
            <p className="font-display text-base font-bold uppercase tracking-wide text-ink">This section is being populated</p>
            <p className="mx-auto mt-2 max-w-md text-sm text-neutral-400">Coverage is added continuously. Search the main tuning database or ask our team about your {cat.vehicleNoun}.</p>
            <Link to="/tuning-database" className="mt-6 inline-flex items-center gap-2 bg-regal-amber px-6 py-3 font-display text-xs font-bold uppercase tracking-[0.14em] text-white hover:bg-amber-400 transition-colors" data-testid="tci-db-link">
              Search the Tuning Database
            </Link>
          </div>
        )}

        {!data && !miss && <div className="mt-10 h-96 animate-pulse border border-line bg-white" data-testid="tci-loading" />}

        {data && (
          <div className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="tci-makes">
            {data.makes.map((m, i) => (
              <Reveal key={m.make_slug} delay={Math.min(i * 0.02, 0.3)}>
                <Link
                  to={m.url}
                  data-testid={`tci-make-${m.make_slug}`}
                  className="group flex items-center justify-between border border-line bg-white p-5 transition-colors hover:border-regal-amber"
                >
                  <div>
                    <p className="font-display text-base font-bold uppercase tracking-wide text-ink group-hover:text-regal-amber transition-colors">{m.make}</p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.14em] text-neutral-500">
                      {m.models} model{m.models === 1 ? "" : "s"} · {m.vehicles.toLocaleString()} variant{m.vehicles === 1 ? "" : "s"}
                    </p>
                  </div>
                  <ArrowUpRight size={16} className="shrink-0 text-regal-amber/60 group-hover:text-regal-amber" />
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
